import useAPI from "@toothfairy/shared-api/useApi";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import React, {
  Suspense,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import envConfig from "../../../envConfig";
import Batch from "../../API/Batch";
import batchLogHelper from "../../API/Batch/batchLogHelper";
import Files from "../../API/Files";
import Constants from "../../Constants";
import usePath from "../../Hooks/usePath";
import useWorkspaces from "../../Hooks/useWorkspaces";
import downloader from "../Common/downloader";
import BatchScreen from "./BatchScreen";
import benchmarksHelper from "../../API/Benchmarks/benchmarksHelper";
dayjs.extend(isBetween);

const DEFAULT_BATCH_TYPE = Constants.BATCH_TYPES[0]?.key;

const USE_BATCH_SEED_DATA = false;

const generateBatchSeedData = () => {
  const types = Constants.BATCH_TYPES.map((u) => u.key);
  const statuses = ["completed", "inProgress", "dispatched", "inError"];
  const names = [
    "Sales agent QA",
    "Support deflect",
    "Onboarding check",
    "Billing dispute",
    "Triage v2",
    "Compliance scan",
  ];
  const seed = [];
  for (let i = 0; i < 12; i++) {
    const type = types[i % types.length];
    const status = statuses[i % statuses.length];
    const isABM = type === "abm";
    seed.push({
      id: `seed-${i + 1}`,
      name: `${names[i % names.length]} ${i + 1}`,
      type,
      status,
      createdBy: null,
      _lastChangedAt: Date.now() - i * 86400000,
      batchConfig: { type },
      batchMeta: isABM
        ? {
            duration: 120 + i * 10,
            consistency_summary: { average_consistency_score: 60 + i * 3 },
          }
        : {
            duration: 90 + i * 7,
            scoring_summary: { average_overall_score: 70 + i * 2 },
          },
    });
  }
  return seed;
};

const BatchScreenContainer = ({ navigation }) => {
  const { t } = useTranslation();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { params } = usePath();
  const { getActiveWorkspace, getBatchLogInfo } = useWorkspaces();

  const batchTypes = Constants.BATCH_TYPES;

  const { user } = AppUser.useUser();
  const AppToasty = AppToast.useToast();

  const {
    data: uploadedBatchData,
    loading: batchDataUploadInProgress,
    apiRequest: uploadBatchFile,
    response: uploadBatchFileResponse,
  } = useAPI(Batch.uploadBatchFile, envConfig);

  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);

  const [selectedType, setSelectedType] = useState(DEFAULT_BATCH_TYPE);
  const [typeFilter, setTypeFilter] = useState("all");
  const [start, setStart] = useState(dayjs().startOf("month").toDate());
  const [end, setEnd] = useState(dayjs().endOf("month").toDate());
  const [fileSelector, SetFileSelector] = useState(null);
  const [statusFilter, setStatusFilter] = useState("noFilter");
  const [displayedLogs, setDisplayedLogs] = useState([]);
  const searchTimeoutRef = useRef(null);
  const batchLogsRef = useRef(state?.batchLogs);
  const statusFilterRef = useRef(statusFilter);
  const startRef = useRef(start);
  const endRef = useRef(end);
  const typeFilterRef = useRef(typeFilter);

  useEffect(() => {
    batchLogsRef.current = state?.batchLogs;
  }, [state?.batchLogs]);
  useEffect(() => {
    statusFilterRef.current = statusFilter;
  }, [statusFilter]);
  useEffect(() => {
    startRef.current = start;
  }, [start]);
  useEffect(() => {
    endRef.current = end;
  }, [end]);
  useEffect(() => {
    typeFilterRef.current = typeFilter;
  }, [typeFilter]);

  const [batchConfig, setBatchConfig] = useState({
    temperature:
      getActiveWorkspace()?.parsingConfig?.temperature || Constants.temperature,
    topP: getActiveWorkspace()?.parsingConfig?.topP || Constants.topP,
    maxTokens:
      getActiveWorkspace()?.parsingConfig?.maxTokens || Constants.maxTokens,
    frequencyPenalty:
      getActiveWorkspace()?.parsingConfig?.frequencyPenalty ||
      Constants.frequencyPenalty,
    presencePenalty:
      getActiveWorkspace()?.parsingConfig?.presencePenalty ||
      Constants.presencePenalty,
    fileUploadRequired: true,
    isQuestionGeneration: false,
    weights: {
      correctness: 80,
      reasoning: 15,
      context_usage: 5,
    },
    passingThreashold: 90,
    mode: "static",
    maxTurns: 15,
    consistencyThreshold: 0.8,
  });
  useEffect(() => {
    if (getActiveWorkspace()?.parsingConfig && !batchConfig)
      setBatchConfig({
        ...getActiveWorkspace()?.parsingConfig,
        fileUploadRequired: true,
        isQuestionGeneration: false,
        name: batchConfig?.name || "",
        description: batchConfig?.description || "",
        weights: {
          correctness: 80,
          reasoning: 15,
          context_usage: 5,
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(getActiveWorkspace()?.parsingConfig)]);

  const loadBatchLogs = async (workspaceID) => {
    let resultLogs = await batchLogHelper?.getBatchLogsByWorkspaceID(
      workspaceID,
    );
    resultLogs = resultLogs?.data?.batchJobLogsByWorkspace?.items?.filter(
      (u) => u?._deleted != true,
    );
    resultLogs = resultLogs?.map((u) => {
      return {
        ...u,
        batchMeta: JSON.parse(u?.batchMeta),
        batchConfig: JSON.parse(u?.batchConfig),
      };
    });
    dispatch("SET_BATCH_LOGS", resultLogs);
  };

  const currentSource = () =>
    USE_BATCH_SEED_DATA ? generateBatchSeedData() : batchLogsRef.current;

  const computeFilteredLogs = (batchLogs) => {
    const currentTypeFilter = typeFilterRef.current;
    const currentStatus = statusFilterRef.current;
    const currentStart = startRef.current;
    const currentEnd = endRef.current;
    return batchLogs
      ?.filter(
        (u) =>
          (currentTypeFilter === "all" || u.type === currentTypeFilter) &&
          (u.status === currentStatus || currentStatus === "noFilter") &&
          dayjs(new Date(u._lastChangedAt)).isBetween(
            currentStart,
            currentEnd,
            null,
            "[]",
          ),
      )
      ?.map((u) => {
        return {
          ...u,
          batchConfig:
            typeof u?.batchConfig == "string"
              ? JSON.parse(u?.batchConfig)
              : u?.batchConfig,
          batchMeta:
            typeof u?.batchMeta == "string"
              ? JSON.parse(u?.batchMeta)
              : u?.batchMeta,
        };
      });
  };

  const debouncedSearch = useCallback((searchParams = {}) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      if (searchParams.typeFilter !== undefined)
        typeFilterRef.current = searchParams.typeFilter;
      if (searchParams.statusFilter !== undefined)
        statusFilterRef.current = searchParams.statusFilter;
      if (searchParams.start !== undefined)
        startRef.current = searchParams.start;
      if (searchParams.end !== undefined) endRef.current = searchParams.end;
      setDisplayedLogs(computeFilteredLogs(currentSource()));
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (v) => {
    setStatusFilter(v);
    debouncedSearch({ statusFilter: v });
  };
  const handleTypeFilterChange = (v) => {
    setTypeFilter(v);
    debouncedSearch({ typeFilter: v });
  };
  const handleFieldUpdate = async (id, property, value) => {
    try {
      const result = await batchLogHelper.updateBatchJobLog(id, [
        {
          property,
          value,
        },
      ]);
      const objectIndex = state?.batchLogs.findIndex((log) => log.id === id);
      if (objectIndex >= 0) {
        state.batchLogs[objectIndex] = result;
        dispatch("SET_BATCH_LOGS", state.batchLogs);
      }
    } catch (error) {
      throw error;
    }
  };
  const handleUpdateStartDate = (v) => {
    setStart(v);
    debouncedSearch({ start: v });
  };
  const handleUpdateEndDate = (v) => {
    setEnd(v);
    debouncedSearch({ end: v });
  };
  const handleNoFileUpload = async () => {
    await uploadBatchFile({
      workspaceid: state?.activeWorkspaceID,
      type: "rc",
      userid: user?.id,
      token: getActiveWorkspace().workspaceToken,
      config: batchConfig,
    });
  };
  useEffect(() => {
    if (fileSelector) {
      handleFileSelect();
      SetFileSelector(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileSelector]);
  const handleBatchInitiation = async (v) => {
    try {
      const refinedValue = {
        name: `${v.name}-${
          state?.benchmarks?.find((u) => u?.id == v.benchmarkID)?.name
        }`,
        description: v.description,
        agentID: v.agentId,
        reviewerAgentID: v.reviewerAgentID,
        benchmarkID: v.benchmarkID,
        status: "dispatched",
        type: selectedType,
        workspaceID: getActiveWorkspace()?.id,
        batchConfig: JSON.stringify({
          ...v,
          type: selectedType,
          examinerAgentId:
            selectedType === "abm" ? v.examinerAgentId : undefined,
        }),
        createdBy: user?.id,
      };
      const batchLogResult = await batchLogHelper.createBatchJobLog(
        refinedValue,
      );

      loadBatchLogs(getActiveWorkspace()?.id);
    } catch (error) {
      console.error("Error on batch initiation", error);
      AppToasty.show(t("batchParsingCreationFailedCaption"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  const handleCancelBatch = async (id) => {
    try {
      const result = await batchLogHelper.deleteBatchJobLog(id);
      AppToasty.show(t("BatchDeletionCaption"), {
        placement: "top",
        type: "success",
      });
      const batchLogs = state?.batchLogs.filter((u) => u.id !== id);
      dispatch("SET_BATCH_LOGS", batchLogs);
    } catch (error) {
      AppToasty.show(t("BatchDeletionFailedCaption"), {
        placement: "top",
        type: "danger",
      });
    }
  };

  useEffect(() => {
    const source = USE_BATCH_SEED_DATA
      ? generateBatchSeedData()
      : state?.batchLogs;
    setDisplayedLogs(computeFilteredLogs(source));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.batchLogs]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  const loadBenchmarks = async (workspaceID) => {
    try {
      let resultBenchmarks = await benchmarksHelper?.getBenchmarksByWorkspaceID(
        workspaceID,
      );
      resultBenchmarks = resultBenchmarks?.data?.BenchmarksByWorkspace?.items?.filter(
        (u) => u?._deleted != true,
      );
      dispatch("SET_BENCHMARKS", resultBenchmarks);
    } catch (error) {
      console.error("Error loading benchmarks:", error);
    } finally {
    }
  };
  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadBenchmarks(getActiveWorkspace()?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  const handleFileSelect = () => {
    if (Platform.OS === "web") {
      fileSelector.value = null;
      fileSelector.click();
    } else {
      AppToasty.show(
        t("toasts.bulkParsingNotAvailable", { platform: Platform.OS }),
        {
          type: "warning",
        },
      );
    }
  };
  const handleBatchOutputDataDownload = async ({
    workspaceid,
    logId,
    type,
    selection,
  }) => {
    try {
      const result = await Promise.all(
        selection?.map((u) =>
          S3downloadUrlRequest({
            workspaceid,
            logId: logId,
            type: getBatchLogInfo(logId)?.type?.toLowerCase(),
            filename: Constants.BATCH_DOWNLOAD_OPTIONS.find(
              (item) => item.id === u,
            ).filename,
            context: "batch",
          }),
        ),
      );

      downloader.download_files(
        result?.map((u, i) => {
          return {
            download: u.url,
            filename: "BT-" + logId + "-" + selection[i],
          };
        }),
      );
    } catch (error) {
      console.error("Batch data download error", error);
    }
  };
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <BatchScreen
        handleFieldUpdate={handleFieldUpdate}
        onUpdateStartDate={handleUpdateStartDate}
        onUpdateEndDate={handleUpdateEndDate}
        onFilterChange={handleFilterChange}
        statusFilter={statusFilter}
        startDate={start}
        endDate={end}
        typeFilter={typeFilter}
        setTypeFilter={handleTypeFilterChange}
        batchTypes={batchTypes}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        handleBatchInitiation={handleBatchInitiation}
        handleFileSelect={handleFileSelect}
        batchLogs={displayedLogs}
        hasAnyData={(USE_BATCH_SEED_DATA ? generateBatchSeedData() : state?.batchLogs)?.length > 0}
        handleCancelBatch={handleCancelBatch}
        batchParsingInProgress={
          state?.batchLogs?.filter(
            (u) => u.status === "inProgress" || u.status === "dispatched",
          )?.length > 0
        }
        batchConfig={batchConfig}
        setBatchConfig={setBatchConfig}
        handleBatchOutputDataDownload={handleBatchOutputDataDownload}
      />
    </Suspense>
  );
};

export default BatchScreenContainer;
